(function () {
  'use strict';

  if (window.UnifiedJourney) {
    return;
  }

  const VERSION = '1.1.0';

  const STORAGE_KEY =
    'ut_journey_v1';

  const MAX_ENTRIES = 100;

  /*
   * Only these event fields may enter Journey.
   * No PII. No raw payloads.
   */
  const SAFE_EVENT_FIELDS = [
    'event_category',
    'service',
    'section_name',
    'section_index',
    'click_location',
    'cta_target',
    'inquiry_type',
    'form_location',
    'share_method',
    'social_platform',
    'conversion_candidate'
  ];

  let sequence = 0;

  /*
   * ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------
   */

  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }

  function isDebugEnabled() {
    return (
      getConfig().debug === true
    );
  }

  function getContext() {
    return (
      window.UnifiedContext ||
      null
    );
  }

  function isPlainObject(value) {
    return Boolean(
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    );
  }

  function clone(value) {
    try {
      return JSON.parse(
        JSON.stringify(value)
      );
    } catch (error) {
      return value;
    }
  }

  function nowIso() {
    return new Date()
      .toISOString();
  }

  /*
   * ------------------------------------------------------------
   * STORAGE
   * ------------------------------------------------------------
   */

  function storageRead() {
    try {
      const raw =
        window.sessionStorage
          .getItem(
            STORAGE_KEY
          );

      if (!raw) {
        return [];
      }

      const parsed =
        JSON.parse(raw);

      return Array.isArray(
        parsed
      )
        ? parsed
        : [];
    } catch (error) {
      return [];
    }
  }

  function storageWrite(
    entries
  ) {
    try {
      window.sessionStorage
        .setItem(
          STORAGE_KEY,
          JSON.stringify(
            entries
          )
        );

      return true;
    } catch (error) {
      return false;
    }
  }

  function saveEntry(
    entry
  ) {
    const entries =
      storageRead();

    entries.push(
      entry
    );

    if (
      entries.length >
      MAX_ENTRIES
    ) {
      entries.splice(
        0,
        entries.length -
          MAX_ENTRIES
      );
    }

    storageWrite(
      entries
    );

    return clone(
      entry
    );
  }

  /*
   * ------------------------------------------------------------
   * BUS
   * ------------------------------------------------------------
   */

  function emit(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      try {
        return window.UnifiedBus.emit(
          type,
          data || {}
        );
      } catch (error) {
        // Legacy fallback below.
      }
    }

    try {
      window.dispatchEvent(
        new CustomEvent(
          'unified:debug',
          {
            detail: {
              type:
                type,

              timestamp:
                nowIso(),

              data:
                data || {}
            }
          }
        )
      );
    } catch (error) {
      // Best effort only.
    }

    return null;
  }

  /*
   * ------------------------------------------------------------
   * CONTEXT HELPERS
   * ------------------------------------------------------------
   */

  function getSessionId() {
    const context =
      getContext();

    if (
      context &&
      typeof context.getSessionId ===
        'function'
    ) {
      return context
        .getSessionId();
    }

    return '';
  }

  function getPageSequence() {
    const context =
      getContext();

    if (
      context &&
      typeof context.getPageSequence ===
        'function'
    ) {
      return context
        .getPageSequence();
    }

    return 0;
  }

  function getPagePath() {
    const context =
      getContext();

    if (
      context &&
      typeof context.getPagePath ===
        'function'
    ) {
      return context
        .getPagePath();
    }

    return (
      window.location &&
      window.location.pathname
        ? window.location.pathname
        : '/'
    );
  }

  /*
   * ------------------------------------------------------------
   * ENTRY CREATION
   * ------------------------------------------------------------
   */

  function nextSequence() {
    sequence += 1;

    return sequence;
  }

  function createBaseEntry(
    type
  ) {
    return {
      id:
        nextSequence(),

      type:
        type,

      timestamp:
        nowIso(),

      ut_session_id:
        getSessionId(),

      page_sequence:
        getPageSequence(),

      page_path:
        getPagePath()
    };
  }

  /*
   * ------------------------------------------------------------
   * SAFE EVENT EXTRACTION
   * ------------------------------------------------------------
   */

  function sanitizeEventPayload(
    payload
  ) {
    const source =
      isPlainObject(
        payload
      )
        ? payload
        : {};

    const result = {};

    SAFE_EVENT_FIELDS
      .forEach(
        function (
          fieldName
        ) {
          if (
            !Object.prototype
              .hasOwnProperty.call(
                source,
                fieldName
              )
          ) {
            return;
          }

          const value =
            source[
              fieldName
            ];

          if (
            value === null ||
            typeof value ===
              'undefined'
          ) {
            return;
          }

          if (
            typeof value ===
              'string'
          ) {
            result[
              fieldName
            ] =
              value
                .trim()
                .slice(
                  0,
                  150
                );

            return;
          }

          if (
            typeof value ===
              'number' ||
            typeof value ===
              'boolean'
          ) {
            result[
              fieldName
            ] =
              value;
          }
        }
      );

    return result;
  }

  /*
   * ------------------------------------------------------------
   * PAGE JOURNEY
   * ------------------------------------------------------------
   */

  function recordPageLoad() {
    const existing =
      storageRead();

    const pageSequence =
      getPageSequence();

    const pagePath =
      getPagePath();

    /*
     * Avoid duplicate page records if
     * initialization runs twice.
     */
    const duplicate =
      existing.some(
        function (
          entry
        ) {
          return (
            entry.type ===
              'page' &&
            entry.page_sequence ===
              pageSequence &&
            entry.page_path ===
              pagePath
          );
        }
      );

    if (duplicate) {
      return null;
    }

    const entry =
      Object.assign(
        createBaseEntry(
          'page'
        ),
        {
          service:
            (
              window.UnifiedContext &&
              typeof window.UnifiedContext
                .resolveService ===
                'function'
            )
              ? window.UnifiedContext
                  .resolveService(
                    '',
                    {}
                  )
              : 'general'
        }
      );

    saveEntry(
      entry
    );

    emit(
      'journey_page_recorded',
      {
        page_sequence:
          entry.page_sequence,

        page_path:
          entry.page_path,

        service:
          entry.service
      }
    );

    if (
      isDebugEnabled()
    ) {
      console.info(
        '[Unified Journey] Page recorded',
        {
          pageSequence:
            entry.page_sequence,

          pagePath:
            entry.page_path,

          service:
            entry.service
        }
      );
    }

    return clone(
      entry
    );
  }

  /*
   * ------------------------------------------------------------
   * EVENT JOURNEY
   * ------------------------------------------------------------
   */

  function recordEvent(
    eventName,
    payload,
    options
  ) {
    if (
      typeof eventName !==
        'string' ||
      !eventName.trim()
    ) {
      return null;
    }

    const safePayload =
      sanitizeEventPayload(
        payload
      );

    const settings =
      isPlainObject(
        options
      )
        ? options
        : {};

    const entry =
      Object.assign(
        createBaseEntry(
          'event'
        ),
        {
          event:
            eventName
              .trim()
              .toLowerCase(),

          status:
            typeof settings.status ===
              'string'
              ? settings.status
                  .trim()
                  .toLowerCase()
              : 'recorded'
        },
        safePayload
      );

    saveEntry(
      entry
    );

    emit(
      'journey_event_recorded',
      {
        event:
          entry.event,

        status:
          entry.status,

        page_sequence:
          entry.page_sequence,

        page_path:
          entry.page_path,

        service:
          entry.service ||
          ''
      }
    );

    if (
      isDebugEnabled()
    ) {
      console.info(
        '[Unified Journey] Event recorded',
        {
          event:
            entry.event,

          status:
            entry.status,

          pageSequence:
            entry.page_sequence,

          service:
            entry.service ||
            ''
        }
      );
    }

    return clone(
      entry
    );
  }

  /*
   * ------------------------------------------------------------
   * OUTCOME RECORDING
   * ------------------------------------------------------------
   */

  function recordOutcome(
    eventName,
    status,
    meta
  ) {
    if (
      typeof eventName !==
        'string' ||
      !eventName.trim()
    ) {
      return null;
    }

    const allowedStatuses = [
      'approved',
      'blocked',
      'dispatched',
      'failed'
    ];

    const normalizedStatus =
      String(
        status || ''
      )
        .trim()
        .toLowerCase();

    const finalStatus =
      allowedStatuses.includes(
        normalizedStatus
      )
        ? normalizedStatus
        : 'recorded';

    const sourceMeta =
      isPlainObject(
        meta
      )
        ? meta
        : {};

    const entry =
      Object.assign(
        createBaseEntry(
          'outcome'
        ),
        {
          event:
            eventName
              .trim()
              .toLowerCase(),

          status:
            finalStatus
        }
      );

    /*
     * Only non-sensitive operational metadata.
     */
    if (
      typeof sourceMeta.reason ===
        'string'
    ) {
      entry.reason =
        sourceMeta.reason
          .trim()
          .slice(
            0,
            150
          );
    }

    if (
      Array.isArray(
        sourceMeta.providers
      )
    ) {
      entry.providers =
        sourceMeta.providers
          .filter(
            function (
              provider
            ) {
              return (
                typeof provider ===
                'string'
              );
            }
          )
          .map(
            function (
              provider
            ) {
              return provider
                .trim()
                .toLowerCase();
            }
          )
          .slice(
            0,
            10
          );
    }

    saveEntry(
      entry
    );

    emit(
      'journey_outcome_recorded',
      {
        event:
          entry.event,

        status:
          entry.status,

        page_sequence:
          entry.page_sequence,

        reason:
          entry.reason ||
          ''
      }
    );

    return clone(
      entry
    );
  }

  /*
   * ------------------------------------------------------------
   * QUERY API
   * ------------------------------------------------------------
   */

  function getJourney() {
    return clone(
      storageRead()
    );
  }

  function getPages() {
    return getJourney()
      .filter(
        function (
          entry
        ) {
          return (
            entry.type ===
            'page'
          );
        }
      );
  }

  function getEvents() {
    return getJourney()
      .filter(
        function (
          entry
        ) {
          return (
            entry.type ===
            'event'
          );
        }
      );
  }

  function getOutcomes() {
    return getJourney()
      .filter(
        function (
          entry
        ) {
          return (
            entry.type ===
            'outcome'
          );
        }
      );
  }

  function getSummary() {
    const entries =
      getJourney();

    const pages =
      entries.filter(
        function (
          entry
        ) {
          return (
            entry.type ===
            'page'
          );
        }
      );

    const events =
      entries.filter(
        function (
          entry
        ) {
          return (
            entry.type ===
            'event'
          );
        }
      );

    const outcomes =
      entries.filter(
        function (
          entry
        ) {
          return (
            entry.type ===
            'outcome'
          );
        }
      );

    return {
      version:
        VERSION,

      ut_session_id:
        getSessionId(),

      entries:
        entries.length,

      pages:
        pages.length,

      events:
        events.length,

      outcomes:
        outcomes.length,

      current_page_sequence:
        getPageSequence(),

      current_page_path:
        getPagePath()
    };
  }

  function printJourney() {
    const entries =
      getJourney();

    console.table(
      entries.map(
        function (
          entry
        ) {
          return {
            id:
              entry.id,

            type:
              entry.type,

            page:
              entry.page_sequence,

            path:
              entry.page_path,

            event:
              entry.event ||
              '',

            service:
              entry.service ||
              '',

            status:
              entry.status ||
              '',

            location:
              entry.click_location ||
              entry.form_location ||
              entry.section_name ||
              ''
          };
        }
      )
    );

    return entries;
  }

  function clear() {
    try {
      window.sessionStorage
        .removeItem(
          STORAGE_KEY
        );

      sequence = 0;

      if (
        isDebugEnabled()
      ) {
        console.info(
          '[Unified Journey] Journey cleared.'
        );
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  /*
   * ------------------------------------------------------------
   * INITIALIZATION
   * ------------------------------------------------------------
   */

  const previousEntries =
    storageRead();

  if (
    previousEntries.length
  ) {
    sequence =
      previousEntries.reduce(
        function (
          maximum,
          entry
        ) {
          const id =
            Number(
              entry.id
            );

          return (
            Number.isFinite(id) &&
            id > maximum
              ? id
              : maximum
          );
        },
        0
      );
  }

  window.UnifiedJourney = {
    version:
      VERSION,

    recordPageLoad:
      recordPageLoad,

    recordEvent:
      recordEvent,

    recordOutcome:
      recordOutcome,

    getJourney:
      getJourney,

    getPages:
      getPages,

    getEvents:
      getEvents,

    getOutcomes:
      getOutcomes,

    getSummary:
      getSummary,

    printJourney:
      printJourney,

    clear:
      clear,

    safeFields:
      SAFE_EVENT_FIELDS.slice()
  };

  /*
   * Context must already exist at this point
   * in the canonical loading order.
   */
  recordPageLoad();

  if (
    isDebugEnabled()
  ) {
    console.info(
      '[Unified Journey] Journey initialized',
      {
        version:
          VERSION,

        entries:
          storageRead()
            .length,

        sessionId:
          getSessionId(),

        pageSequence:
          getPageSequence()
      }
    );
  }
})();